import { createSelector } from 'reselect';
import { RootPatronState } from '../../common/use-patron-selector';
import { Feature, getFeatureFlag } from '../features/features';

const program = (state: RootPatronState) => state.program || {};

// Factory function to create feature flag selectors
const createFeatureFlagSelector = (feature: Feature) =>
  createSelector([(state: RootPatronState) => state], (state) =>
    getFeatureFlag(state, feature)
  );

const getProgram = createSelector([program], (program) => program);

const getProgramId = createSelector([getProgram], (program) => program.id);

const getProgramName = createSelector([getProgram], (program) => program.name);

const getProgramBrandId = createSelector(
  [getProgram],
  (program) => program.brand_id
);

const getProgramBrandName = createSelector(
  [getProgram],
  (program) => program.brand_name
);

const getProgramOrgSlug = createSelector(
  [getProgram],
  (program) => program.org_slug
);

const getProgramLoginUrl = createSelector(
  [getProgram],
  (program) => program.login_url
);

const getProgramSessionIsSSO = createSelector(
  [getProgram],
  (program) => program.session_is_sso
);

const getProgramSLOEnabled = createFeatureFlagSelector(
  Feature.SP_INITIATED_SLO_ENABLED
);

const getProgramLogoUrl = createSelector(
  [getProgram],
  (program) => program.logo_image_url
);

const getProgramHeaderUrl = createSelector(
  [getProgram],
  (program) => program.header_image_url
);

const getProgramThemeIcon = createSelector(
  [getProgram],
  (program) => program.theme.icon_image_url
);

const getProgramThemeColor = createSelector(
  [getProgram],
  (program) => program.theme.theme_color
);

const getProgramSecondaryColor = createSelector(
  [getProgram],
  (program) => program.theme.secondary_color
);

const getProgramWallpaper = createSelector(
  [getProgram],
  (program) => program.theme.wallpaper_image_url
);

const getProgramHiddenProfileFields = createSelector(
  [getProgram],
  (program) => program.hidden_profile_values || []
);

const getProgramStrongPasswordRequired = createSelector(
  [getProgram],
  (program) => !!program.strong_password_required
);

const getProgramPasswordValidations = createSelector(
  [getProgram],
  (program) => program.password_validations
);

const getProgramGdprComplianceEnabled = createSelector(
  [getProgram],
  (program) => !!program.gdpr_compliance_enabled
);

const getProgramGdprComplianceContacts = createSelector(
  [getProgram],
  (program) => program.gdpr_compliance_contacts
);

const getProgramCanSubmitContent = createSelector(
  [getProgram],
  (program) => !!program.allow_mobile_content_submission
);

const getProgramCanShareContent = createSelector(
  [getProgram],
  (program) => !!program.content_sharing_enabled
);

const getProgramCanCommentContent = createSelector(
  [getProgram],
  (program) => !!program.commenting_enabled
);

const getProgramCanTranslateContent = createSelector(
  [getProgram],
  (program) => !!program.content_translations_enabled
);

const getProgramResourcesEnabled = createSelector(
  [getProgram],
  (program) => !!program.resources_enabled
);

const getProgramInCaseYouMissedItEnabled = createSelector(
  [getProgram],
  (program) => !!program.content_digest_enabled
);

const getProgramFromTheCommunityEnabled = createSelector(
  [getProgram],
  (program) => !!program.from_the_community_enabled
);

const getV2NotesEnabled = createSelector(
  [getProgram],
  (program) => !!program.v2_notes_enabled
);

const getV2CommentPlaceholderEnabled = createSelector(
  [getProgram],
  (program) => !!program.v2_comment_placeholder_enabled
);

const getAssistantHomePageExperimentEnabled = createSelector(
  [getProgram, createFeatureFlagSelector(Feature.NOTIFICATION_CENTER_ENABLED)],
  (program, notificationCenterEnabled) =>
    program.assistant_home_page_experiment_customization_enabled &&
    !notificationCenterEnabled
);

const getDirectoryExtraFieldsEnabled = createFeatureFlagSelector(
  Feature.DIRECTORY_EXTRA_FIELDS_ENABLED
);

const getCommentThinkWarningEnabled = createFeatureFlagSelector(
  Feature.COMMENT_THINK_WARNING_ENABLED
);

const getCommentThinkWarningHtml = createSelector(
  [getProgram],
  (program) => program.comment_think_warning_html
);

const getCommentThinkWarningLink = createSelector(
  [getProgram],
  (program) => program.comment_think_warning_link
);

export const getSanitizedContentEnabled = createSelector(
  [getProgram],
  (program) => !!program.sanitized_content_enabled
);

const getSubmissionsCommentDefaultEnabled = createSelector(
  [getProgram],
  (program) => !!program.commenting_default_enabled
);

const getMaxVideoUploadSizeInBytes = createSelector(
  [getProgram],
  (program) => program.max_video_upload_size_in_bytes
);

const getSwitchCommunitiesEnabled = createSelector(
  [getProgram],
  (program) => !!program.switch_communities_enabled
);

const getImagesInArticlesEnabled = createSelector(
  [getProgram],
  (program) => !!program.images_in_articles_enabled
);

const getForYouFeedEnabled = createFeatureFlagSelector(
  Feature.CONTENT_FOR_YOU_FEED_ENABLED
);

const getEmailTokenAuthEnabled = createSelector(
  [getProgram],
  (program) => !!program.email_token_auth_enabled
);

const getAdvancedProfileEnabled = createFeatureFlagSelector(
  Feature.ADVANCED_PROFILE_ENABLED
);

const getProfileEditNameEnabled = createSelector(
  [getProgram],
  (program) => !!program.profile_edit_name_enabled
);

//deprecated when new content detail is enabled
const getCommentSortEnabled = createFeatureFlagSelector(
  Feature.COMMENTS_SORT_ENABLED
);

const getFeedCardViewsEnabled = createFeatureFlagSelector(
  Feature.FEED_CARD_VIEWS_ENABLED
);

const getOrgChartEnabled = createSelector(
  [getProgram],
  (program) => !!program.org_chart_enabled
);

const getRelatedContentEnabled = createSelector(
  [getProgram],
  (program) => !!program.related_content_enabled
);

const getExpandedReactionTypesEnabled = createSelector(
  [
    getProgram,
    createFeatureFlagSelector(Feature.EXPANDED_REACTION_TYPES_DEPLOY),
  ],
  (program, expandedReactionTypesDeployEnabled) =>
    !!program.expanded_reaction_types_customization_enabled &&
    expandedReactionTypesDeployEnabled
);

export default {
  getProgram,
  getProgramId,
  getProgramName,
  getProgramBrandId,
  getProgramBrandName,
  getProgramOrgSlug,
  getProgramLoginUrl,
  getProgramSessionIsSSO,
  getProgramSLOEnabled,
  getProgramLogoUrl,
  getProgramHeaderUrl,
  getProgramThemeIcon,
  getProgramThemeColor,
  getProgramSecondaryColor,
  getProgramWallpaper,
  getProgramHiddenProfileFields,
  getProgramStrongPasswordRequired,
  getProgramPasswordValidations,
  getProgramGdprComplianceEnabled,
  getProgramGdprComplianceContacts,
  getProgramCanSubmitContent,
  getProgramCanShareContent,
  getProgramCanCommentContent,
  getProgramCanTranslateContent,
  getProgramResourcesEnabled,
  getProgramInCaseYouMissedItEnabled,
  getProgramFromTheCommunityEnabled,
  getV2NotesEnabled,
  getV2CommentPlaceholderEnabled,
  getAssistantHomePageExperimentEnabled,
  getDirectoryExtraFieldsEnabled,
  getCommentThinkWarningEnabled,
  getCommentThinkWarningHtml,
  getCommentThinkWarningLink,
  getSanitizedContentEnabled,
  getSubmissionsCommentDefaultEnabled,
  getMaxVideoUploadSizeInBytes,
  getSwitchCommunitiesEnabled,
  getImagesInArticlesEnabled,
  getForYouFeedEnabled,
  getEmailTokenAuthEnabled,
  getAdvancedProfileEnabled,
  getProfileEditNameEnabled,
  getCommentSortEnabled,
  getFeedCardViewsEnabled,
  getOrgChartEnabled,
  getRelatedContentEnabled,
  getExpandedReactionTypesEnabled,
};
